import React, { useEffect, useState } from 'react';
import { Button, Card, Badge } from '../components/UI';
import { UserProfile, UserRole } from '../types';
import { Trash2, LogIn, Users, Shield, ArrowLeft } from 'lucide-react';

interface AdminDashboardProps {
  currentUser: UserProfile;
  onLoginAsUser: (user: any) => void;
  onBack: () => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ currentUser, onLoginAsUser, onBack }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/admin', {
        headers: {
          'x-admin-id': currentUser.id
        }
      });
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = async (userId: string) => {
    if (!confirm("Are you sure? This will delete the user and all their assets permanently.")) return;
    
    try {
      await fetch(`/api/admin?userId=${userId}`, {
        method: 'DELETE',
        headers: { 'x-admin-id': currentUser.id }
      });
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      alert("Failed to delete");
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}><ArrowLeft size={20}/></Button>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        </div>
        <div className="bg-brand-primary text-white px-4 py-2 rounded-full font-bold shadow-lg">
          {users.length} Active Users
        </div>
      </div>

      <div className="grid gap-4">
        {isLoading && <div className="text-center py-10">Loading users...</div>}
        
        {!isLoading && users.map(user => (
          <div key={user.id} className="bg-white p-6 rounded-2xl shadow-sm border border-brand-text/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl ${user.role === 'ADMIN' ? 'bg-red-100 text-red-600' : 'bg-gray-100'}`}>
                {user.companyName.charAt(0)}
              </div>
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  {user.companyName}
                  {user.role === 'ADMIN' && <Badge color="bg-red-500 text-white">Admin</Badge>}
                  {user.role === 'AGENCY' && <Badge color="bg-brand-secondary">Agency</Badge>}
                </h3>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-8 text-sm text-gray-500">
               <div>
                 <span className="block font-bold text-black">{user.asset_count}</span> Assets
               </div>
               <div>
                 <span className="block font-bold text-black capitalize">{user.pricingPlan.replace('_', ' ')}</span> Plan
               </div>
               <div>
                 <span className="block font-bold text-black">{new Date(user.createdAt).toLocaleDateString()}</span> Joined
               </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={() => onLoginAsUser(user)} variant="outline" className="text-xs">
                <LogIn size={14} /> Impersonate
              </Button>
              {user.id !== currentUser.id && (
                <Button onClick={() => deleteUser(user.id)} variant="ghost" className="text-red-500 hover:bg-red-50">
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};